/**
 * InfoButton Component
 * Small info icon button that triggers modals
 */

import { designTokens, borderRadius } from '../../constants/designTokens';
import { useLanguage } from '../../i18n';

interface InfoButtonProps {
  onClick: () => void;
  ariaLabel?: string;
}

export function InfoButton({ onClick, ariaLabel }: InfoButtonProps) {
  const { t } = useLanguage();
  const resolvedAriaLabel = ariaLabel || t('common.moreInfo');

  return (
    <>
      <button 
        className="info-button" 
        onClick={onClick}
        aria-label={resolvedAriaLabel}
        type="button"
      >
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 16 16" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle 
            cx="8" 
            cy="8" 
            r="7" 
            stroke="currentColor" 
            strokeWidth="1.5"
          />
          <path 
            d="M8 12V8M8 4h.01" 
            stroke="currentColor" 
            strokeWidth="1.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          />
          <circle cx="8" cy="4" r="0.5" fill="currentColor"/>
        </svg>
      </button>

      <style>{`
        .info-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          padding: ${designTokens.spacing.xs};
          cursor: pointer;
          color: ${designTokens.colors.text.secondary};
          transition: all 0.2s ease;
          border-radius: ${borderRadius.full};
          margin-left: ${designTokens.spacing.xs};
        }

        .info-button:hover {
          color: ${designTokens.colors.brand.primary};
          background-color: ${designTokens.colors.withOpacity.brandLight};
        }

        .info-button:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px ${designTokens.colors.withOpacity.brandFocus};
        }

        .info-button svg {
          width: 16px;
          height: 16px;
        }

        ${designTokens.media.desktop} {
          .info-button svg {
            width: 18px;
            height: 18px;
          }
        }
      `}</style>
    </>
  );
}
